import React from "react";

import PopupContainer from "components/PopupContainer";

import {
  CheckedIcon,
  ForwardIcon,
  ImageIcon,
  ChatIcon,
} from "components/icons";

// Define types for better type safety
interface UpgradePopupProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  items: Array<{
    mainText: string;
    subText: string;
  }>;
  borderColor: string;
  bgColor: string;
  textColor: string;
}

// Reusable components
const FeatureCard: React.FC<FeatureItem> = ({
  icon,
  title,
  items,
  borderColor,
  bgColor,
  textColor,
}) => (
  <div
    className="flex flex-col p-4 rounded-xl border-l-4"
    style={{ borderColor, backgroundColor: bgColor }}
  >
    <div className="flex items-center gap-2.5 mb-2.5">
      {icon}
      <p className="font-semibold text-sm" style={{ color: textColor }}>
        {title}
      </p>
    </div>
    {items.map((item, index) => (
      <div
        key={index}
        className="bg-white p-2.5 rounded-md flex items-center gap-3 mb-2 last:mb-0"
      >
        <div
          className="h-[22px] w-[22px] flex items-center justify-center rounded-full flex-shrink-0"
          style={{ backgroundColor: bgColor }}
        >
          <CheckedIcon width={10} height={12} color={textColor} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[#171717] font-semibold text-sm">
            {item.mainText}
          </span>
          <span className="text-[#737373] text-xs leading-4">
            {item.subText}
          </span>
        </div>
      </div>
    ))}
  </div>
);

const UpgradePopup: React.FC<UpgradePopupProps> = ({
  open,
  onClose,
  onUpgrade,
}) => {
  // Feature data
  const features: FeatureItem[] = [
    {
      icon: <ImageIcon />,
      title: "Image Generation",
      items: [
        {
          mainText: "330+ images per month",
          subText: "That's 1000 credits total!",
        },
      ],
      borderColor: "#6466F1",
      bgColor: "#EEF2FF",
      textColor: "#6466F1",
    },
    {
      icon: <ChatIcon />,
      title: "DeftGPT Document Chat",
      items: [
        {
          mainText: "Access top AI models",
          subText: "GPT-4o, Gemini 2.0, Claude Sonnet 3.7",
        },
        {
          mainText: "Document chatting",
          subText: "PDFs, Excel, CSVs and more...",
        },
        {
          mainText: "Generate code, proposals, pitch",
          subText: "Unlock your productivity!",
        },
      ],
      borderColor: "#A855F7",
      bgColor: "#FAF5FF",
      textColor: "#A855F7",
    },
  ];

  return (
    <PopupContainer open={open} onClose={onClose}>
      {/* <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Upgrade Plan Popup"
      >
        <div
          className="bg-white rounded-lg w-full max-w-[517px] mx-4 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        > */}
      {/* Header Section */}
      <header
        className="text-white rounded-t-lg p-5 text-center"
        style={{
          background: "linear-gradient(90deg, #6565F0 0%, #EA499C 100%)",
          borderTop: "0.8px dashed #4338CA",
        }}
      >
        <div className="w-12 h-12 bg-[rgba(254,243,199,0.10)] rounded-full flex items-center justify-center mx-auto mb-2.5">
          <img
            src="/icons/lightning-icon.png"
            className="max-w-full h-auto"
            alt="Upgrade indicator"
          />
        </div>
        <h2 className="text-xl font-bold">You're out of credits!</h2>
        <p className="mt-1 text-sm font-medium text-white/80">
          Upgrade to unlock 330+ image generations & DefGPT Chat!
        </p>
      </header>

      {/* Pricing Section */}
      <section className="p-6">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="text-sm font-semibold text-[#94A3B8] line-through">
            $14.99
          </span>
          <span className="text-[#171717] text-3xl font-bold">$11.99</span>
          <span className="text-[#525252] text-sm font-medium">/month</span>
        </div>

        {/* Features List */}
        <div className="space-y-4 max-w-[311px] mx-auto mb-6">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>

        {/* Upgrade Button */}
        <button
          onClick={onUpgrade}
          className="w-full max-w-[311px] mx-auto bg-[#5046E6] text-white text-[13px] h-8 rounded-md flex items-center justify-center gap-2 shadow-md hover:bg-[#4338CA] transition-colors duration-200"
          aria-label="Upgrade subscription plan"
        >
          <span>Upgrade Now</span>
          <ForwardIcon />
        </button>

        {/* Cancel Text */}
        <p className="text-center text-xs text-[#737373] mt-3 font-medium">
          Cancel anytime. No commitment required.
        </p>
      </section>
      {/* </div>
      </div> */}
    </PopupContainer>
  );
};

export default UpgradePopup;
